import React, { useMemo } from 'react';
import { Clock4, Users } from 'lucide-react';
import { getReservationDateTime, getReservationGuestName, getReservationTimeValue, isReservationOnDay } from './utils';

function buildSlots(startHour = 8, endHour = 24, intervalMinutes = 15) {
  const slots = [];
  for (let hour = startHour; hour < endHour; hour += 1) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      slots.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  return slots;
}

function reservationOverlapsSlot(reservation, slotLabel, selectedDay) {
  if (!isReservationOnDay(reservation, selectedDay)) return false;

  const startDate = getReservationDateTime(reservation);
  if (!startDate) return false;

  const [slotHour, slotMinute] = slotLabel.split(':').map((value) => Number(value || 0));
  const slotDate = new Date(selectedDay);
  slotDate.setHours(slotHour, slotMinute, 0, 0);

  const endDate = new Date(startDate);
  endDate.setMinutes(endDate.getMinutes() + Number(reservation.duration_minutes || 90));

  return startDate <= slotDate && endDate > slotDate;
}

export default function SlotsOverviewView({ reservations = [], selectedDay }) {
  const slots = useMemo(() => buildSlots(), []);

  const slotRows = useMemo(() => {
    return slots.map((slotLabel) => {
      const overlapping = reservations.filter((reservation) =>
        reservationOverlapsSlot(reservation, slotLabel, selectedDay),
      );
      const covers = overlapping.reduce((sum, reservation) => sum + Number(reservation.party_size || 0), 0);

      return {
        slotLabel,
        reservations: overlapping,
        covers,
      };
    });
  }, [reservations, selectedDay, slots]);

  return (
    <div className="h-full overflow-auto rounded-xl border bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Clock4 className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-800">Slot-Ueberblick</h3>
      </div>

      <div className="space-y-2">
        {slotRows.map((slot) => {
          const loadWidth = Math.min(100, slot.covers * 6);
          return (
            <div key={slot.slotLabel} className="rounded-lg border bg-slate-50/70 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-[72px] text-sm font-semibold text-slate-800">{slot.slotLabel}</div>
                <div className="flex flex-1 items-center gap-3">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                    <div className="h-full rounded-full bg-[#71c545]" style={{ width: `${loadWidth}%` }} />
                  </div>
                  <div className="flex min-w-[84px] items-center justify-end gap-1 text-xs font-medium text-slate-500">
                    <Users className="h-3.5 w-3.5" />
                    <span>{slot.covers} Covers</span>
                  </div>
                </div>
              </div>

              {slot.reservations.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {slot.reservations.map((reservation) => (
                    <span key={`${slot.slotLabel}-${reservation.id}`} className="inline-flex rounded-full border bg-white px-2 py-0.5 text-[11px] text-slate-600">
                      {getReservationTimeValue(reservation)} · {getReservationGuestName(reservation)} · {reservation.party_size || 0}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-400">Keine Ueberschneidungen in diesem Slot.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
